// src/modules/app-views/products/pages/Document/modals/ProductsParametersModal/tabs/BrandsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import BrandFormModal from './BrandFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BrandsTab = ({ loading, setLoading, moduleId: moduleIdProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');
  const moduleId = moduleIdProp || storedModule?.id || 'products';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for brands - initialize from store, update locally after save
  const [brands, setBrands] = useState(() => {
    const rawBrands = appService.getModulePreferences({ moduleId, category: 'brands' });
    return normalizeBrands(rawBrands);
  });

  // Filter out removed brands for display in table
  const visibleBrands = useMemo(() => brands.filter(b => !b.removed), [brands]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed brands)
      const isDuplicate = brands.some(
        b =>
          b.uid !== editingRecord?.uid &&
          !b.removed &&
          b.label.toLowerCase() === formData.label.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedBrands;

        if (editingRecord) {
          // Editing existing brand - keep the same uid and preserve removed flag
          updatedBrands = brands.map(b =>
            b.uid === editingRecord.uid
              ? { ...b, label: formData.label }
              : b
          );
        } else {
          // Adding new brand - generate new uid
          updatedBrands = [
            ...brands,
            {
              uid: generateUidV4(),
              label: formData.label,
            },
          ];
        }

        // Save array directly: brands: [...]
        await appService.updateModulePreferences({
          moduleId,
          category: 'brands',
          data: updatedBrands,
        });

        // Update local state immediately (don't wait for SSE)
        setBrands(updatedBrands);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[BrandsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [brands, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        // Check if brand is in use via API
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'brands',
          uid: record.uid,
        });

        let updatedBrands;

        if (isInUse) {
          // Soft delete: mark as removed but keep in array
          updatedBrands = brands.map(b =>
            b.uid === record.uid ? { ...b, removed: true } : b
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          // Hard delete: remove from array
          updatedBrands = brands.filter(b => b.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        // Save array directly: brands: [...]
        await appService.updateModulePreferences({
          moduleId,
          category: 'brands',
          data: updatedBrands,
        });

        // Update local state immediately (don't wait for SSE)
        setBrands(updatedBrands);
      } catch (error) {
        console.error('[BrandsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [brands, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('brand'),
      dataIndex: 'label',
      render: text => <span className="font-weight-bold">{text}</span>,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleBrands}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_brand')}
        </Button>
      </Flex>

      <BrandFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BrandsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeBrands(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeBrand);
}

function normalizeBrand(brand) {
  return {
    uid: brand.uid,
    label: brand.label || '',
    ...(brand.removed && { removed: true }),
  };
}
